import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { FiInfo } from "react-icons/fi";
import { useCurrentCompanyQuery } from "features/Company/hooks";
import { useUserDetailQuery } from "features/User/hooks";
import { Company } from "models/company";
import { PeopleTeamSettingsForm } from "./PeopleTeamSettingsForm";

function ContactRow({ label, userId, tooltip }: { label: string; userId?: string; tooltip?: string }) {
  const { data: user, isLoading } = useUserDetailQuery(userId || "", { enabled: !!userId });

  return (
    <Flex justify="space-between" align="center" py={2}>
      <HStack spacing={1} align="center" w="220px">
        <Text fontSize="sm" color="content.tertiary">
          {label}
        </Text>
        {tooltip && (
          <Tooltip label={tooltip} placement="top" hasArrow maxW="260px">
            <span>
              <Icon as={FiInfo} boxSize={3} color="content.tertiary" cursor="default" />
            </span>
          </Tooltip>
        )}
      </HStack>
      <Skeleton isLoaded={!isLoading} minW="160px">
        <Text fontSize="sm" color="content.primary">
          {userId ? (user?.fullName ?? "—") : "Not configured"}
        </Text>
      </Skeleton>
    </Flex>
  );
}

export default function PeopleTeamSettings() {
  const { onOpen, onClose, isOpen } = useDisclosure();
  const { data, isLoading } = useCurrentCompanyQuery();
  const company = data as Company;

  return (
    <Box>
      <Skeleton isLoaded={!isLoading}>
        <Card minWidth="900px" width="calc(100% - 200px)" mt="4">
          <CardBody>
            <Flex justifyContent="space-between" w="100%" alignItems="center" p={5}>
              <Box opacity="70%" fontSize="2xl">
                People Team Contacts
              </Box>
              <Button onClick={onOpen} px="20px">
                Edit
              </Button>
            </Flex>
            <VStack align="stretch" px={10} divider={<Box borderBottomWidth="1px" borderColor="gray.100" />}>
              <ContactRow
                label="HR Question Contact"
                userId={company?.hrQuestionContactId}
                tooltip="New hires can message this person on Slack when they have questions. Shown as 'Ask a Question' on the new hire portal."
              />
              <ContactRow
                label="Default People Team Contact"
                userId={company?.defaultPeopleTeamContactId}
                tooltip="Automatically assigned as the People Team contact on new hire journeys when no specific contact is set."
              />
            </VStack>
          </CardBody>
        </Card>
      </Skeleton>

      <Modal size="xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>People Team Contacts</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {company && <PeopleTeamSettingsForm company={company} onClose={onClose} />}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
